import React, { useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate } from "react-router-dom";
import AppRoutes from "./routes";
import ClearOTPOnExit from "./component/ClearOTPOnExit";
import { openDB } from "./demo/utilities/indexedDB";
import SecureLS from "secure-ls";
import versionInfo from "./version.json";
import {
  cacheMobileAlias,
  getRouteSegmentForPosId,
  isMongoId,
  isV2Pos,
  resolvePosId,
} from "./utils/posIdUtils";

import { createAppTheme } from "./theme/theme";

const ls = new SecureLS({ encodingType: "aes" });

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [themeVersion, setThemeVersion] = React.useState(0);
  const [resolvingMobileRoute, setResolvingMobileRoute] = useState(false);
  const theme = React.useMemo(() => createAppTheme(), [themeVersion]);

  useEffect(() => {
    openDB()
      .then(() => {
        console.log("IndexedDB initialized");
      })
      .catch((error) => {
        console.error("Failed to initialize IndexedDB", error);
      });
  }, []);

  
  React.useEffect(() => {
    const onColors = () => setThemeVersion((v) => v + 1);
    window.addEventListener("theme-colors-updated", onColors);
    return () => window.removeEventListener("theme-colors-updated", onColors);
  }, []);

  useEffect(() => {
    const match = location.pathname.match(/^\/mobile\/([^/]+)(.*)$/i);
    if (!match) return;
    const [, seg, rest = ""] = match;
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    let active = true;

    const resolveMobileRoute = async () => {
      const cachedPosId = resolvePosId(seg);
      if (!apiBase) {
        if (isMongoId(seg)) {
          const routed = getRouteSegmentForPosId(seg);
          if (routed && routed !== seg) {
            navigate(`/mobile/${routed}${rest}${location.search || ""}`, {
              replace: true,
            });
          }
        }
        return;
      }

      if (!isMongoId(seg) && cachedPosId !== seg) return;

      if (active) setResolvingMobileRoute(true);
      try {
        const response = await fetch(
          `${apiBase}/v1/api/stores/resolve/${encodeURIComponent(seg)}`
        );
        if (!response.ok) return;
        const store = await response.json();
        if (!active) return;

        const aliasForCache = store?.mobileAlias || (!isMongoId(seg) ? seg : "");
        if (aliasForCache && store?.posId) {
          cacheMobileAlias(aliasForCache, store.posId);
        }

        if (isMongoId(seg) && store?.mobileAlias) {
          navigate(
            `/mobile/${encodeURIComponent(store.mobileAlias)}${rest}${
              location.search || ""
            }`,
            { replace: true }
          );
        }
      } catch {
        // Unknown aliases fall through to the existing not-found/API handling.
      } finally {
        if (active) setResolvingMobileRoute(false);
      }
    };

    resolveMobileRoute();
    return () => {
      active = false;
    };
  }, [location.pathname, location.search, navigate]);

  const user = ls.get("user");
  const showUserName = /^\/[^/]+\/order$/.test(location.pathname);
  const isMobile = location.pathname.startsWith("/mobile");

  const hideWatermark =
    location.pathname === "/sales" ||
    location.pathname === "/views/claiming" ||
    location.pathname.startsWith("/views/claiming/");

  const routePosId = (() => {
    const mobileMatch = location.pathname.match(/^\/mobile\/([^/]+)/i);
    if (mobileMatch?.[1]) return mobileMatch[1];

    const orderMatch = location.pathname.match(/^\/([^/]+)\/order(?:\/|$)/i);
    if (orderMatch?.[1]) return orderMatch[1];

    const landingMatch = location.pathname.match(/^\/([^/]+)$/i);
    if (landingMatch?.[1]) {
      const reserved = new Set([
        "login",
        "signup",
        "dashboard",
        "admin-dashboard",
        "sales",
        "salesstats",
        "inventory",
        "menucreation",
        "account-management",
        "access-management",
        "reports",
        "customers",
        "config",
        "config-service-charge",
        "config-gross-sales",
        "config-kiosk",
        "config-kds",
        "config-terms",
        "views",
        "vouchers",
        "notifications",
        "promotions",
        "payment",
        "back",
        "mobile",
        "taccustomer",
        "privacycustomer",
        "c",
        "404",
      ]);
      const slug = landingMatch[1].toLowerCase();
      if (!reserved.has(slug)) return landingMatch[1];
    }

    const qsPosId = new URLSearchParams(location.search || "").get("posId");
    return qsPosId || "";
  })();

  const resolvedVersionPosId = routePosId || user?.posId || "";
  const appVersion = isV2Pos(resolvedVersionPosId) ? "0.1.1" : "0.1.0";

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ClearOTPOnExit />
      {!resolvingMobileRoute && <AppRoutes />}

      {!hideWatermark && (
        <Box
          sx={{
            position: "fixed",
            bottom: isMobile ? 1 : 8,
            right: isMobile ? 1 : 8,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            color: "white",
            padding: "6px 10px",
            borderRadius: 1,
            zIndex: 9999,
            userSelect: "none",
            fontSize: "0.75rem",
            lineHeight: 1.2,
            opacity: 0.8,
            transform: isMobile ? "scale(0.24)" : "scale(1)",
            transformOrigin: "bottom right",
            "&:hover": {
              opacity: 1,
            },
          }}
        >
          <Typography variant="caption" fontWeight="medium">
            Sparkle POS v {appVersion} (#{versionInfo.buildNumber})
          </Typography>
          {showUserName && user && (
            <Typography
              variant="caption"
              fontWeight="regular"
              sx={{
                display: "block",
                marginTop: "2px",
                fontSize: isMobile ? "0.5rem" : "0.7rem",
              }}
            >
              {user.firstname} {user.lastname}
            </Typography>
          )}
        </Box>
      )}
    </ThemeProvider>
  );
};

export default App;
